/**
 * The sound effect engine: plays registered sounds (sfx/registry.ts) into a master SFX bus, one
 * bus per category under it, with a voice pool that caps polyphony, a seeded pitch variation per
 * call, stereo pan and distance attenuation for world sounds, and bookkeeping of live nodes.
 *
 * It owns no AudioContext: `Sfx` (audio.ts) makes one on the first user gesture and attaches the
 * engine to its effects bus, so the player's volume and mute apply to everything played here.
 */
import { mulberry32 } from '@nine/shared';
import { KitShared, SfxKit } from './kit';
import { trimOf } from './levels';
import './load';
import {
  getSfx,
  SFX_CATEGORIES,
  SFX_CATEGORY_GAIN,
  VoicePool,
  type SfxCategory,
  type SfxDef,
  type SfxOpts,
} from './registry';

/** Beyond this many tiles a world sound is not played at all. */
export const SFX_MAX_DIST = 22;
/** Distance (tiles) at which a world sound is at half strength. */
export const SFX_REF_DIST = 7;

/** Level of a sound `dist` tiles away (1 close by, 0 beyond `SFX_MAX_DIST`). */
export function attenuation(dist: number): number {
  if (dist >= SFX_MAX_DIST) return 0;
  const near = 1 / (1 + (Math.max(0, dist - 1.5) / SFX_REF_DIST) ** 2);
  // A soft edge, so a sound does not cut out at the limit.
  const edge = Math.min(1, (SFX_MAX_DIST - dist) / 4);
  return near * edge;
}

/** A sound under way: loops are stopped through this; levels and pan can follow the source. */
export interface SfxHandle {
  readonly id: string;
  stop(fade?: number): void;
  /** Strength as at `play` (`intensity`), gliding there. */
  setLevel(v: number, glide?: number): void;
  setPan(p: number, glide?: number): void;
  readonly alive: boolean;
}

interface Voice {
  key: number;
  def: SfxDef;
  kit: SfxKit;
  bus: GainNode;
  panner: StereoPannerNode | null;
  /** The sound's own level and trim (what an intensity of 1 sets the bus to). */
  base: number;
  nodes: number;
  done: boolean;
}

export interface SfxStats {
  /** Voices sounding now, and their nodes. */
  voices: number;
  nodes: number;
  /** The most there have been at once. */
  peakVoices: number;
  peakNodes: number;
  played: number;
  /** Too far away, too soon after the same sound, or no room left. */
  culled: number;
  /** Cut short to make room for a newer sound. */
  stolen: number;
  byCategory: Record<string, number>;
}

export class SfxEngine {
  ctx: BaseAudioContext | null = null;
  private master: GainNode | null = null;
  private buses = new Map<SfxCategory, GainNode>();
  private shared: KitShared | null = null;
  private pool = new VoicePool();
  private voices = new Map<number, Voice>();
  private lastAt = new Map<string, number>();
  private seq = 1;
  private warned = new Set<string>();
  private liveNodes = 0;
  private st = { peakVoices: 0, peakNodes: 0, played: 0, culled: 0, stolen: 0 };
  /**
   * While a world event is being turned into visuals (render/effects.ts), the event mapping
   * (sfx/events.ts) has already played its sounds: the old effect calls made there stay silent.
   */
  claimed = false;
  /** Makes (or resumes) the audio context from a user gesture: set by `Sfx` (audio.ts). */
  unlocker: (() => void) | null = null;

  /** Plays into `out` (the game's effects bus, or a context's destination). */
  attach(ctx: BaseAudioContext, out: AudioNode): void {
    if (this.ctx === ctx) return;
    this.stopAll(0);
    this.ctx = ctx;
    this.shared = new KitShared(ctx);
    // Master: a gentle level, then a limiter so a pile-up of hits never clips.
    const master = ctx.createGain();
    master.gain.value = 0.8;
    const limit = ctx.createDynamicsCompressor();
    limit.threshold.value = -10;
    limit.knee.value = 6;
    limit.ratio.value = 12;
    limit.attack.value = 0.002;
    limit.release.value = 0.15;
    master.connect(limit).connect(out);
    this.master = master;
    this.buses.clear();
    for (const c of SFX_CATEGORIES) {
      const g = ctx.createGain();
      g.gain.value = SFX_CATEGORY_GAIN[c];
      g.connect(master);
      this.buses.set(c, g);
    }
    this.pool = new VoicePool();
    this.voices.clear();
    this.lastAt.clear();
    this.liveNodes = 0;
  }

  /** Asks for the audio context from a user gesture (the front screens' first click). */
  requestUnlock(): void {
    this.unlocker?.();
  }

  /** Level of the whole SFX bus (0–1), under the player's volume. */
  setMaster(v: number): void {
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(v, this.ctx.currentTime, 0.05);
  }

  /** An old effect call (`Sfx.swing()` …): silent while the event mapping has spoken for the event. */
  legacy(id: string, o?: SfxOpts): void {
    if (!this.claimed) this.play(id, o);
  }

  play(id: string, o: SfxOpts = {}): SfxHandle | null {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || !this.shared) return null;
    // A live context waits for a gesture; nothing is scheduled into a suspended one.
    if (typeof AudioContext !== 'undefined' && ctx instanceof AudioContext && ctx.state !== 'running')
      return null;
    const def = getSfx(id);
    if (!def) {
      if (!this.warned.has(id)) {
        this.warned.add(id);
        console.warn(`sfx: no sound '${id}'`);
      }
      return null;
    }
    const now = ctx.currentTime;
    const intensity = Math.max(0, Math.min(1.5, o.intensity ?? 1));
    let level = intensity * (def.gain ?? 1) * trimOf(id);
    if (o.dist !== undefined) level *= attenuation(o.dist);
    if (level < 0.015) {
      this.st.culled++;
      return null;
    }
    const last = this.lastAt.get(id);
    if (!def.loop && last !== undefined && now - last < (def.gap ?? 0.03)) {
      this.st.culled++;
      return null;
    }
    const adm = this.pool.admit(def.cat, id, now, !!def.loop);
    if (!adm.ok) {
      this.st.culled++;
      return null;
    }
    for (const k of adm.steal) this.cut(k, 0.02);
    this.lastAt.set(id, now);

    const seed = o.seed ?? Math.imul(this.seq++, 2654435761);
    const rng = mulberry32(seed >>> 0);
    const vary = def.vary ?? 0.04;
    const pitch = (o.pitch ?? 1) * (def.pitch ?? 1) * (1 + (rng() * 2 - 1) * vary);
    const bus = ctx.createGain();
    bus.gain.value = level;
    let panner: StereoPannerNode | null = null;
    if (o.pan !== undefined || def.loop) {
      panner = ctx.createStereoPanner();
      panner.pan.value = Math.max(-1, Math.min(1, o.pan ?? 0));
      bus.connect(panner).connect(this.buses.get(def.cat)!);
    } else bus.connect(this.buses.get(def.cat)!);
    const start = now + Math.max(0, o.delay ?? 0) + 0.004;
    const kit = new SfxKit(ctx, bus, start, pitch, rng, this.shared, !!def.loop, { intensity, len: o.len });
    const base = (def.gain ?? 1) * trimOf(id);
    const v: Voice = { key: adm.key, def, kit, bus, panner, base, nodes: 0, done: false };
    try {
      def.play(kit, { intensity, len: o.len });
    } catch (e) {
      console.error(`sfx '${id}' failed`, e);
      for (const s of kit.sources) s.disconnect();
      bus.disconnect();
      this.pool.release(adm.key);
      return null;
    }
    v.nodes = kit.nodes + 1 + (panner ? 1 : 0);
    this.voices.set(adm.key, v);
    this.liveNodes += v.nodes;
    this.st.played++;
    this.st.peakVoices = Math.max(this.st.peakVoices, this.voices.size);
    this.st.peakNodes = Math.max(this.st.peakNodes, this.liveNodes);
    // The voice is over when its last source stops.
    if (!def.loop) {
      const lastSrc = kit.last;
      if (lastSrc) lastSrc.onended = () => this.finish(v);
      else this.finish(v);
      // Should an `ended` never come (a context closed mid-sound), let go after its length.
      if (typeof AudioContext !== 'undefined' && ctx instanceof AudioContext) {
        const ms = (kit.end - now + 1) * 1000;
        setTimeout(() => this.finish(v), Math.min(30_000, Math.max(100, ms)));
      }
    }
    return this.handle(v);
  }

  private handle(v: Voice): SfxHandle {
    return {
      id: v.def.id,
      stop: (fade = 0.3) => this.cut(v.key, fade),
      setLevel: (level, glide = 0.1) => {
        if (!v.done && this.ctx) v.bus.gain.setTargetAtTime(level * v.base, this.ctx.currentTime, glide);
      },
      setPan: (p, glide = 0.1) => {
        if (!v.done && this.ctx && v.panner)
          v.panner.pan.setTargetAtTime(Math.max(-1, Math.min(1, p)), this.ctx.currentTime, glide);
      },
      get alive() {
        return !v.done;
      },
    };
  }

  /** Fades a voice out and stops its sources. */
  private cut(key: number, fade: number): void {
    const v = this.voices.get(key);
    if (!v || v.done || !this.ctx) {
      this.pool.release(key);
      return;
    }
    const now = this.ctx.currentTime;
    v.bus.gain.cancelScheduledValues(now);
    v.bus.gain.setValueAtTime(v.bus.gain.value, now);
    v.bus.gain.linearRampToValueAtTime(0, now + Math.max(0.005, fade));
    for (const s of v.kit.sources) {
      try {
        s.stop(now + Math.max(0.005, fade) + 0.02);
      } catch {
        /* already stopped */
      }
    }
    this.st.stolen += fade <= 0.03 ? 1 : 0;
    const last = v.kit.sources[v.kit.sources.length - 1];
    if (last) last.onended = () => this.finish(v);
    else this.finish(v);
    // Every source now stops by then; `finish` is idempotent.
    if (typeof setTimeout !== 'undefined') setTimeout(() => this.finish(v), (fade + 0.3) * 1000);
  }

  private finish(v: Voice): void {
    if (v.done) return;
    v.done = true;
    this.pool.release(v.key);
    this.voices.delete(v.key);
    this.liveNodes -= v.nodes;
    try {
      v.bus.disconnect();
      v.panner?.disconnect();
    } catch {
      /* gone with its context */
    }
  }

  stopAll(fade = 0.2): void {
    for (const k of [...this.voices.keys()]) this.cut(k, fade);
  }

  stats(): SfxStats {
    const byCategory: Record<string, number> = {};
    for (const v of this.voices.values()) byCategory[v.def.cat] = (byCategory[v.def.cat] ?? 0) + 1;
    return { voices: this.voices.size, nodes: this.liveNodes, ...this.st, byCategory };
  }
}

/** The game's engine (attached by audio.ts on the first gesture). */
export const sfxEngine = new SfxEngine();
